import { CURRENT_ONBOARDING_VERSION, OnboardingFlow } from '@mondaygpt/constants'
import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit'
import { fetchCurrentUserAction, logout } from 'modules/authentication'
import { selectCurrentUser } from 'modules/common'
import { RootState } from 'types'
import { ONBOARDING_STATE_PARAM, OnboardingUrlState } from '../types'

const ONBOARDING_STATE_STORAGE_KEY = 'onboarding_url_state'
const LANDING_URL_STORAGE_KEY = 'onboarding_landing_url'
const PROMO_CODE_PARAM = 'promo'
const PROMO_CODE_STORAGE_KEY = 'promo_code'

// Mirrors the server's PROMO_CODE_PATTERN, case-insensitive since we normalize
// before sending. Garbage params are dropped instead of carried through auth.
const PROMO_CODE_URL_PATTERN = /^[A-Za-z0-9][A-Za-z0-9_-]{2,31}$/

export type OnboardingStateSource = 'url' | 'redirectUrl' | 'sessionStorage' | 'none'

interface OnboardingViewState {
  // Decoded state from URL
  onboardingFlow?: OnboardingFlow
  createFromTemplate?: string
  duplicateAgent?: string

  // Promo code from ?promo=CODE, carried through auth to the paywall
  pendingPromoCode?: string

  // Track onboarding completion
  onboardingCompleted: boolean

  // URL params have been processed
  onboardingUrlStateInitialized: boolean

  // User data has been loaded (onboardingCompleted is now reliable)
  onboardingUserStateInitialized: boolean

  // Debug: where was the state found
  stateSource: OnboardingStateSource
}

const initialState: OnboardingViewState = {
  onboardingCompleted: false,
  onboardingUrlStateInitialized: false,
  onboardingUserStateInitialized: false,
  stateSource: 'none',
}

/**
 * Validates that a string is a valid OnboardingFlow enum value
 */
function isValidOnboardingFlow(value: unknown): value is OnboardingFlow {
  return (
    typeof value === 'string' && Object.values(OnboardingFlow).includes(value as OnboardingFlow)
  )
}

/**
 * Parses the base64-encoded state parameter from the URL
 */
function parseBase64State(stateParam: string | null): OnboardingUrlState | null {
  if (!stateParam) return null

  try {
    const decoded = atob(decodeURIComponent(stateParam))
    const parsed = JSON.parse(decoded)

    // Validate and extract only known keys
    return {
      onboardingFlow: isValidOnboardingFlow(parsed.onboardingFlow)
        ? parsed.onboardingFlow
        : undefined,
      createFromTemplate:
        typeof parsed.createFromTemplate === 'string' ? parsed.createFromTemplate : undefined,
      duplicateAgent: typeof parsed.duplicateAgent === 'string' ? parsed.duplicateAgent : undefined,
    }
  } catch {
    console.warn('Failed to parse onboarding state from URL:', stateParam)
    return null
  }
}

/**
 * Saves onboarding state to sessionStorage as backup for page reloads during auth
 */
function saveStateToSession(state: OnboardingUrlState): void {
  try {
    sessionStorage.setItem(ONBOARDING_STATE_STORAGE_KEY, JSON.stringify(state))
    // Also save the landing URL for debug purposes
    if (!sessionStorage.getItem(LANDING_URL_STORAGE_KEY)) {
      sessionStorage.setItem(LANDING_URL_STORAGE_KEY, window.location.href)
    }
  } catch {
    // sessionStorage might be unavailable in some contexts
  }
}

/**
 * Gets the landing URL from sessionStorage for debug purposes
 */
export function getLandingUrl(): string | null {
  try {
    return sessionStorage.getItem(LANDING_URL_STORAGE_KEY)
  } catch {
    return null
  }
}

/**
 * Checks if sessionStorage is available
 */
export function isSessionStorageAvailable(): boolean {
  try {
    const testKey = '__test__'
    sessionStorage.setItem(testKey, testKey)
    sessionStorage.removeItem(testKey)
    return true
  } catch {
    return false
  }
}

/**
 * Retrieves onboarding state from sessionStorage (backup for page reloads)
 */
function getStateFromSession(): OnboardingUrlState | null {
  try {
    const stored = sessionStorage.getItem(ONBOARDING_STATE_STORAGE_KEY)
    if (!stored) return null
    const parsed = JSON.parse(stored)
    return {
      onboardingFlow: isValidOnboardingFlow(parsed.onboardingFlow)
        ? parsed.onboardingFlow
        : undefined,
      createFromTemplate:
        typeof parsed.createFromTemplate === 'string' ? parsed.createFromTemplate : undefined,
      duplicateAgent: typeof parsed.duplicateAgent === 'string' ? parsed.duplicateAgent : undefined,
    }
  } catch {
    return null
  }
}

/**
 * Clears onboarding state from sessionStorage (called when onboarding completes)
 */
function clearStateFromSession(): void {
  try {
    sessionStorage.removeItem(ONBOARDING_STATE_STORAGE_KEY)
    sessionStorage.removeItem(LANDING_URL_STORAGE_KEY)
    sessionStorage.removeItem(PROMO_CODE_STORAGE_KEY)
  } catch {
    // Ignore errors
  }
}

/**
 * Resolves the ?promo=CODE param with the same triple-fallback as the
 * onboarding state: URL → nested redirectUrl → sessionStorage backup.
 */
function resolvePromoCode(searchParams: URLSearchParams): string | undefined {
  let promo = searchParams.get(PROMO_CODE_PARAM)

  if (!promo) {
    const redirectUrl = searchParams.get('redirectUrl')
    if (redirectUrl) {
      try {
        const decodedRedirectUrl = decodeURIComponent(redirectUrl)
        const redirectUrlParams = new URLSearchParams(
          decodedRedirectUrl.includes('?')
            ? decodedRedirectUrl.substring(decodedRedirectUrl.indexOf('?'))
            : ''
        )
        promo = redirectUrlParams.get(PROMO_CODE_PARAM)
      } catch {
        // Ignore decoding errors
      }
    }
  }

  promo = promo?.trim() ?? null
  if (promo && PROMO_CODE_URL_PATTERN.test(promo)) {
    try {
      sessionStorage.setItem(PROMO_CODE_STORAGE_KEY, promo)
    } catch {
      // sessionStorage might be unavailable in some contexts
    }
    return promo
  }

  try {
    const stored = sessionStorage.getItem(PROMO_CODE_STORAGE_KEY)
    if (stored && PROMO_CODE_URL_PATTERN.test(stored)) return stored
  } catch {
    // Ignore errors
  }
  return undefined
}

/**
 * Reads the pending promo code (URL → redirectUrl → sessionStorage) without
 * touching Redux or the URL. Used to stamp first-touch analytics attribution
 * before APP_OPENED fires, which happens earlier than initOnboardingState.
 */
export function peekPromoCode(): string | undefined {
  try {
    return resolvePromoCode(new URLSearchParams(window.location.search))
  } catch {
    return undefined
  }
}

function removePromoCodeFromUrl(): void {
  const url = new URL(window.location.href)
  if (url.searchParams.has(PROMO_CODE_PARAM)) {
    url.searchParams.delete(PROMO_CODE_PARAM)
    window.history.replaceState({}, '', url.toString())
  }
}

/**
 * Removes the onboarding state param from the current URL without navigation
 */
function removeOnboardingStateFromUrl(): void {
  const url = new URL(window.location.href)
  if (url.searchParams.has(ONBOARDING_STATE_PARAM)) {
    url.searchParams.delete(ONBOARDING_STATE_PARAM)
    window.history.replaceState({}, '', url.toString())
  }
}

const slice = createSlice({
  name: 'onboardingView',
  initialState,
  reducers: {
    /**
     * Initialize onboarding state from URL params.
     * Should be called once at app startup.
     * Checks: 1) URL directly, 2) inside redirectUrl param, 3) sessionStorage backup
     *
     * The sessionStorage backup is crucial for surviving page reloads during OAuth flows.
     */
    initOnboardingState: state => {
      if (state.onboardingUrlStateInitialized) return

      const searchParams = new URLSearchParams(window.location.search)
      let stateSource: OnboardingStateSource = 'none'

      // First, check for onboardingState directly in the URL
      let stateParam = searchParams.get(ONBOARDING_STATE_PARAM)
      if (stateParam) {
        stateSource = 'url'
      }

      // If not found, check inside the redirectUrl parameter
      if (!stateParam) {
        const redirectUrl = searchParams.get('redirectUrl')
        if (redirectUrl) {
          try {
            const decodedRedirectUrl = decodeURIComponent(redirectUrl)
            const redirectUrlParams = new URLSearchParams(
              decodedRedirectUrl.includes('?')
                ? decodedRedirectUrl.substring(decodedRedirectUrl.indexOf('?'))
                : ''
            )
            stateParam = redirectUrlParams.get(ONBOARDING_STATE_PARAM)
            if (stateParam) {
              stateSource = 'redirectUrl'
            }
          } catch {
            // Ignore decoding errors
          }
        }
      }

      let parsedState = parseBase64State(stateParam)

      // If we found state in URL, save to sessionStorage as backup for page reloads
      if (parsedState) {
        saveStateToSession(parsedState)
        // Clean up URL after storing
        removeOnboardingStateFromUrl()
      } else {
        // Fallback: check sessionStorage (survives page reloads during OAuth)
        parsedState = getStateFromSession()
        if (parsedState) {
          stateSource = 'sessionStorage'
        }
      }

      if (parsedState) {
        state.onboardingFlow = parsedState.onboardingFlow
        state.createFromTemplate = parsedState.createFromTemplate
        state.duplicateAgent = parsedState.duplicateAgent
      }

      state.pendingPromoCode = resolvePromoCode(searchParams)
      removePromoCodeFromUrl()

      state.stateSource = stateSource
      state.onboardingUrlStateInitialized = true
    },

    /**
     * Drops the pending promo code once it has been applied at the paywall
     * (or rejected), so reloads don't re-apply it.
     */
    clearPendingPromoCode: state => {
      state.pendingPromoCode = undefined
      try {
        sessionStorage.removeItem(PROMO_CODE_STORAGE_KEY)
      } catch {
        // Ignore errors
      }
    },

    /**
     * Set onboarding as completed
     */
    setOnboardingCompleted: (state, action: PayloadAction<boolean>) => {
      state.onboardingCompleted = action.payload
      // Clear sessionStorage backup when onboarding completes to prevent stale state
      if (action.payload) {
        clearStateFromSession()
      }
    },
  },
  extraReducers: builder => {
    // Set onboardingCompleted based on user data when user is fetched
    builder.addCase(fetchCurrentUserAction.fulfilled, (state, action) => {
      const { user } = action.payload
      state.onboardingCompleted =
        (user.onboardingData?.completedVersion ?? 0) >= CURRENT_ONBOARDING_VERSION
      state.onboardingUserStateInitialized = true
    })

    // Reset state on logout
    builder.addCase(logout, () => {
      clearStateFromSession()
      return initialState
    })
  },
})

export const onboardingViewReducer = slice.reducer

export const { initOnboardingState, setOnboardingCompleted, clearPendingPromoCode } = slice.actions

// Selectors
export const selectOnboardingViewState = (state: RootState) => state.onboardingView

/**
 * Select the onboarding flow. Defaults to Default if not specified.
 */
export const selectOnboardingFlow = (state: RootState): OnboardingFlow =>
  state.onboardingView.onboardingFlow ?? OnboardingFlow.Default

export const selectCreateFromTemplate = (state: RootState) =>
  state.onboardingView.createFromTemplate

export const selectDuplicateAgent = (state: RootState) => state.onboardingView.duplicateAgent

export const selectPendingPromoCode = (state: RootState) => state.onboardingView.pendingPromoCode

export const selectOnboardingState = createSelector(
  [selectOnboardingFlow, selectCreateFromTemplate, selectDuplicateAgent],
  (onboardingFlow, createFromTemplate, duplicateAgent): OnboardingUrlState => ({
    onboardingFlow,
    createFromTemplate,
    duplicateAgent,
  })
)

export const selectOnboardingUrlStateInitialized = (state: RootState) =>
  state.onboardingView.onboardingUrlStateInitialized

export const selectOnboardingUserStateInitialized = (state: RootState) =>
  state.onboardingView.onboardingUserStateInitialized

export const selectOnboardingStateSource = (state: RootState) => state.onboardingView.stateSource

/**
 * Returns true if onboarding is completed.
 * Checks the slice state first (set by API), then falls back to persisted user data.
 */
export const selectOnboardingCompleted = createSelector(
  [
    selectOnboardingUserStateInitialized,
    selectCurrentUser,
    (state: RootState) => state.onboardingView.onboardingCompleted,
  ],
  (apiInitialized, currentUser, sliceValue) => {
    // If API has returned, use the slice value (most up-to-date)
    if (apiInitialized) return sliceValue

    // Fall back to persisted user data
    if (currentUser?.onboardingData !== undefined) {
      return (currentUser.onboardingData.completedVersion ?? 0) >= CURRENT_ONBOARDING_VERSION
    }

    // Default to false (not completed)
    return false
  }
)

/**
 * Returns true if we have reliable onboarding data to make routing decisions.
 * This is true if either:
 * 1. The API has returned user data (onboardingUserStateInitialized), OR
 * 2. We have persisted user data with onboardingData from a previous session
 *
 * This prevents showing a loading spinner when user data is already available
 * from Redux persistence.
 */
export const selectOnboardingDataReady = createSelector(
  [selectOnboardingUserStateInitialized, selectCurrentUser],
  (apiInitialized, currentUser) => {
    // API has returned fresh data
    if (apiInitialized) return true

    // We have persisted user data with onboardingData
    if (currentUser?.onboardingData !== undefined) return true

    return false
  }
)
